import { useFormikContext } from 'formik';
import {
  MutableRefObject,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useDigitalOutputBinding } from '../../../../api/hooks/binding/digital-output.binding';
import {
  RealtimePositionEvent,
  RealtimePositionResult,
  useRealtimePositionMonitor,
} from '../../../../api/hooks/binding/realtime-position.binding';
import { useScriptActionBinding } from '../../../../api/hooks/binding/script-action.binding';
import {
  TouchableInputResult,
  useTouchableInputBinding,
} from '../../../../api/hooks/binding/touchable-input.binding';
import { isLocalDev } from '../../../../config/config';
import { useMachineContext } from '../../../../pages/machine/machine-context-provider';
import { QNear, Waypoint } from '../../../../util/machine-variable.utils';
import { useFlowItemFormContext } from '../component/flow-item/edit/flow-item-form.context';
import {
  FlowItemSharedParameterRef,
  TeachIOMapping,
  ToolConfig,
} from '../model/flow.types';
import { RobotTeachError, useCanRobotTeachWithAlert } from './flow-teach.hooks';

export interface WaypointCaptureContextValue {
  ioMapping: TeachIOMapping;
  toolConfig: ToolConfig;
}

export const WaypointCaptureContext = createContext<
  WaypointCaptureContextValue | undefined
>(undefined);

export interface WaypointCaptureConfig {
  innerRef?: MutableRefObject<WaypointCaptureRef | undefined>;
  delayPress?: number;
  delayLongPress?: number;
  onPress?: (position: RealtimePositionEvent | null) => void;
  onLongPress?: () => void;
  onTeachError?: (error: RobotTeachError) => void;
}

export interface WaypointCaptureRef {
  realtimePosition: RealtimePositionResult | undefined;
  forcePress: () => void;
  forceLongPress: () => void;
}

export interface WaypointCaptureContextProviderProps
  extends WaypointCaptureContextValue {
  children: ReactElement;
}

export interface WaypointCaptureProps {
  onCapture?: (waypoint: Waypoint, qnear: QNear) => void;
  onLongPress?: () => void;
  innerRef?: MutableRefObject<WaypointCaptureRef | undefined>;
  onTeachError?: (error: RobotTeachError) => void;
}

interface WaypointFormCaptureProps extends WaypointCaptureProps {
  autoSubmitForm?: boolean;
}

const WaypointCaptureContextProvider = (
  props: WaypointCaptureContextProviderProps
): ReactElement | null => {
  const { ioMapping, toolConfig } = props;

  const value = useMemo<WaypointCaptureContextValue>(() => {
    return {
      ioMapping,
      toolConfig,
    };
  }, [ioMapping, toolConfig]);

  if (value) {
    return (
      <WaypointCaptureContext.Provider value={value}>
        {props.children}
      </WaypointCaptureContext.Provider>
    );
  }

  return null;
};

const useWaypointCaptureContext = () => {
  return useContext(WaypointCaptureContext) as WaypointCaptureContextValue;
};

const useWaypointCaptureBinding = (config: WaypointCaptureConfig): void => {
  const teachError = useCanRobotTeachWithAlert();

  const realtimePosition = useRef<RealtimePositionResult | undefined>();

  const { ioMapping, toolConfig } = useWaypointCaptureContext();
  const { machine } = useMachineContext();
  const { innerRef, onTeachError } = config;

  const machineId = machine.id;

  useEffect(() => {
    if (teachError && typeof onTeachError === 'function') {
      onTeachError(teachError);
    }
  }, [teachError, onTeachError]);

  const onPress = (result?: TouchableInputResult) => {
    if (isLocalDev()) {
      // store original last position and leverage it's data if we can otherwise
      // we will return fake data
      const originalLastPosition =
        realtimePosition.current?.getLastPosition ?? (() => undefined);

      realtimePosition.current = {
        getLastPosition: () => {
          const originalData = originalLastPosition();

          return (
            originalData ?? {
              jointPositions: [0, 0, 0, 0, 0, 0],
              waypoint: {
                rx: 0,
                ry: 0,
                rz: 0,
                x: 0,
                y: 0,
                z: 0,
              },
            }
          );
        },
        isInMotion: false,
      };
    }

    let position: RealtimePositionEvent | null | undefined;

    if (result?.metadata?.pose && result.metadata?.qnear) {
      position = {
        jointPositions: result.metadata.qnear,
        waypoint: result.metadata.pose,
      };
    } else {
      position = realtimePosition.current?.getLastPosition();
    }

    if (position) {
      if (typeof config.onPress === 'function') {
        config.onPress(position);
      }
    }
  };

  if (innerRef) {
    innerRef.current = {
      forceLongPress: () => {
        if (config.onLongPress) {
          config.onLongPress();
        }
      },
      forcePress: () => {
        if (config.onPress) {
          onPress();
        }
      },
      realtimePosition: realtimePosition.current,
    };
  }

  useDigitalOutputBinding(machineId, {
    output: ioMapping.output.light,
    state: true,
    pulse: true,
    pulseInterval: 750,
    finalState: false,
  });

  const setTcpScript = useMemo(() => {
    const torchTcp = toolConfig.tcp;
    const torchCog = toolConfig.cog;
    const torchInteria = toolConfig.inertia;
    const torchMass = toolConfig.mass;

    const tcp = `p[${torchTcp.x}, ${torchTcp.y}, ${torchTcp.z}, ${torchTcp.rx}, ${torchTcp.ry}, ${torchTcp.rz}]`;

    const script = [
      `sec set_tcp():`,
      `\tset_tcp(${tcp})`,
      `\tset_target_payload(${torchMass}, [${torchCog.join(
        ','
      )}], [${torchInteria.join(',')}])`,
      `end`,
    ].join('\n');

    return script;
  }, [toolConfig]);

  useScriptActionBinding(machineId, {
    script: setTcpScript,
    validProgramStates: ['PROGRAM_STOPPED', 'PROGRAM_PAUSED'],
  });

  useTouchableInputBinding(machineId, {
    input: ioMapping.input.capture,
    delayPress: 100,
    delayLongPress: 1200,
    onPress,
    onLongPress: config.onLongPress,
  });

  realtimePosition.current = useRealtimePositionMonitor(machineId, {});
};

const useWaypointCapture = (props: WaypointCaptureProps): void => {
  const { onCapture, onLongPress, innerRef, onTeachError } = props;

  useWaypointCaptureBinding({
    innerRef,
    onPress: (position) => {
      if (position) {
        if (typeof onCapture === 'function') {
          onCapture(position.waypoint, position.jointPositions);
        }
      }
    },
    onLongPress,
    onTeachError,
  });
};

const useUpdateFlowItemFormOnWaypointCapture = ({
  autoSubmitForm = false,
}): ((waypoint: Waypoint, qnear: QNear) => void) => {
  const { setParameterValue } = useFlowItemFormContext();
  const { submitForm } = useFormikContext();

  const onCapture = useCallback(
    (waypoint: Waypoint, qnear: QNear) => {
      if (waypoint && qnear) {
        setParameterValue(FlowItemSharedParameterRef.WAYPOINT, waypoint, false);
        setParameterValue(FlowItemSharedParameterRef.QNEAR, qnear);

        if (typeof onCapture === 'function') {
          if (autoSubmitForm) {
            // add small delay after setting parameter values before
            // submitting form
            setTimeout(() => {
              submitForm();
            }, 10);
          }
        }
      }
    },
    [autoSubmitForm, setParameterValue, submitForm]
  );

  return onCapture;
};

const WaypointFormCapture = (props: WaypointFormCaptureProps): null => {
  const { autoSubmitForm } = props;
  const onCaptureUpdateForm = useUpdateFlowItemFormOnWaypointCapture({
    autoSubmitForm,
  });

  const onCapture = useCallback(
    (waypoint: Waypoint, qnear: QNear) => {
      onCaptureUpdateForm(waypoint, qnear);
      if (props.onCapture) {
        props.onCapture(waypoint, qnear);
      }
    },
    [onCaptureUpdateForm, props]
  );

  useWaypointCapture({ ...props, onCapture });
  return null;
};

export {
  useUpdateFlowItemFormOnWaypointCapture,
  useWaypointCapture,
  useWaypointCaptureBinding,
  WaypointCaptureContextProvider,
  WaypointFormCapture,
};
